import { inject } from '@angular/core';
import { CanActivateFn, Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { NotificationService } from '@progress/kendo-angular-notification';
import { catchError, map, Observable, of, tap } from 'rxjs';
import { ApplicationUserService, User, UserService } from 'shared';
import {
  CrowndeskAccessRight,
  hasAccessRight,
  isRoyalCrownEmployee,
  isSupplierEmployee,
  SUPPLIER_EMPLOYEE_HOME,
} from './access-rights';

/** Only lets users with the given access right (or CrownDesk admins) through; others go to the dashboard. */
export function accessRightGuard(right: CrowndeskAccessRight): CanActivateFn {
  return () => {
    const router = inject(Router);
    const notificationService = inject(NotificationService);
    const translateService = inject(TranslateService);

    return currentUser().pipe(
      map((user) => {
        // Royal Crown staff only get the login-as screen, which replaces the routed pages
        if (isRoyalCrownEmployee(user) || hasAccessRight(user, right)) return true;

        notificationService.show({
          content: translateService.instant('NO_ACCESS_TO_PAGE'),
          animation: { type: 'slide', duration: 200 },
          type: { style: 'warning', icon: true },
          position: { horizontal: 'right', vertical: 'bottom' },
          hideAfter: 3000,
        });
        return router.createUrlTree(['/dashboard']);
      }),
    );
  };
}

/** The supplier employee part: everyone else goes to the dashboard. */
export const supplierEmployeeGuard: CanActivateFn = () => {
  const router = inject(Router);
  return currentUser().pipe(map((user) => isSupplierEmployee(user) || router.createUrlTree(['/dashboard'])));
};

/**
 * Keeps supplier employees out of the client screens. Only looks at an already loaded user: on the first page the
 * AppComponent sends them to their own part once it loaded them, so client employees never wait for /users/me here.
 */
export const clientScreensGuard: CanActivateFn = () =>
  isSupplierEmployee(inject(ApplicationUserService).userSubject.value)
    ? inject(Router).createUrlTree([SUPPLIER_EMPLOYEE_HOME])
    : true;

/**
 * The AppComponent loads the user, but it is only created after the initial navigation
 * (blocking initial navigation), so on a deep link the guard has to fetch the user itself.
 */
function currentUser(): Observable<User | null> {
  const applicationUserService = inject(ApplicationUserService);
  const loadedUser = applicationUserService.userSubject.value;
  if (loadedUser) return of(loadedUser);

  return inject(UserService)
    .getUserMe()
    .pipe(
      tap((user) => {
        applicationUserService.userSubject.next(user);
        applicationUserService.userSettingsSubject.next(user.settings);
      }),
      catchError(() => of(null)),
    );
}
